// Illustrative motion field; coverage is measured from the tracked points.
function mountDripOpticalFlow() {
    const demo = document.querySelector('[data-optical-flow]');
    if (!demo) return () => {};
    const canvas = demo.querySelector('canvas'), ctx = canvas.getContext('2d');
    if (!ctx) return () => {};
    const abort = new AbortController();
    const listen = (target, event, handler) => target.addEventListener(event, handler, { signal: abort.signal });
    const pause = demo.querySelector('[data-flow-pause]');
    const status = demo.querySelector('[data-flow-status]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const darkMode = matchMedia('(prefers-color-scheme: dark)');
    // Resolve the site's shared theme tokens for the canvas as well as the controls.
    function readPalette() {
        const styles = getComputedStyle(demo);
        const token = name => styles.getPropertyValue(name).trim();
        return {
            background: token('--bg-color'), surface: token('--surface-color'),
            ink: token('--heading-color'), muted: token('--muted-color'),
            border: token('--border-color'), accent: token('--link-hover-color'),
            hole: token('--flow-hole-color'), wire: token('--flow-wire-color'),
            shadow: token('--flow-shadow-color'), highlight: token('--flow-highlight-color')
        };
    }
    let palette = readPalette();
    const W = 720, H = 336, threshold = 0.10;
    const mask = document.createElement('canvas');
    mask.width = W / 4; mask.height = H / 4;
    const m = mask.getContext('2d', { willReadFrequently: true });
    const seeds = [];
    for (let y = 12; y < H; y += 24) for (let x = 12; x < W; x += 24) seeds.push({ x, y });
    let elapsed = 0, phase = 'track', hold = 0, keyframe = 1, loss = 0;
    let sceneZoom = 1, sceneShift = 0, sceneTime = 0;
    let seedBall, selected = [];
    let paused = reduced.matches, visible = false, printing = false, last = 0, raf = 0;
    let points = seeds.map(p => ({ ...p }));
    const text = (value, x, y, size = 22, color = palette.muted) => {
        ctx.font = `${size}px system-ui, sans-serif`; ctx.fillStyle = color; ctx.fillText(value, x, y);
    };
    function announce(value) { status.textContent = value; }
    function measure() {
        m.globalCompositeOperation = 'source-over';
        m.fillStyle = palette.hole; m.fillRect(0, 0, mask.width, mask.height);
        m.globalCompositeOperation = 'destination-out';
        m.beginPath();
        points.forEach(p => { m.moveTo(p.x / 4 + 4.5, p.y / 4); m.arc(p.x / 4, p.y / 4, 4.5, 0, Math.PI * 2); });
        m.fill();
        const pixels = m.getImageData(0, 0, mask.width, mask.height).data;
        let uncovered = 0;
        for (let i = 3; i < pixels.length; i += 4) uncovered += pixels[i] / 255;
        loss = uncovered / (mask.width * mask.height);
    }
    function draw() {
        ctx.fillStyle = palette.background; ctx.fillRect(0, 0, 1200, 1100);
        text('01 / SELECT KEYFRAMES', 36, 44, 21, palette.accent);
        text('Coverage gaps trigger a new keyframe.', 36, 83, 27, palette.ink);
        ctx.save(); ctx.translate(36, 110); ctx.beginPath(); ctx.rect(0, 0, W, H); ctx.clip();
        renderScene(ctx, currentScene(), false);
        ctx.globalAlpha = 0.85; ctx.drawImage(mask, 0, 0, W, H); ctx.globalAlpha = 1;
        points.forEach(p => {
            const dx = (p.x - p.seed.x) * 0.16, dy = (p.y - p.seed.y) * 0.16;
            ctx.strokeStyle = palette.accent; ctx.globalAlpha = 0.45; ctx.lineWidth = 1.5;
            ctx.beginPath(); ctx.moveTo(p.x-dx, p.y-dy); ctx.lineTo(p.x,p.y); ctx.stroke(); ctx.globalAlpha = 1;
            ctx.fillStyle = palette.accent; ctx.beginPath(); ctx.arc(p.x,p.y,2.5,0,Math.PI*2); ctx.fill();
        });
        ctx.restore();
        ctx.strokeStyle = phase === 'select' ? palette.hole : phase === 'reset' ? palette.accent : palette.border;
        ctx.lineWidth = phase === 'track' ? 2 : 4; ctx.strokeRect(36,110,W,H);
        text(`KEYFRAME ${keyframe}`, 798, 134, 21, palette.accent);
        text('Coverage loss', 798, 179, 24, palette.ink);
        text(`${(loss*100).toFixed(1)}%`, 798, 242, 52, phase === 'select' ? palette.hole : palette.ink);
        const barX = 798, barY = 272, barWidth = 350;
        ctx.fillStyle = palette.border; ctx.fillRect(barX,barY,barWidth,17);
        ctx.fillStyle = palette.hole; ctx.fillRect(barX,barY,barWidth*Math.min(loss/0.3,1),17);
        const thresholdX = barX+barWidth*threshold/0.3;
        ctx.strokeStyle = palette.ink; ctx.beginPath(); ctx.moveTo(thresholdX,barY-8); ctx.lineTo(thresholdX,barY+25); ctx.stroke();
        text(`${Math.round(threshold * 100)}% selection threshold`, 798, 326, 21);
        text(phase === 'select' ? 'Threshold reached' : phase === 'complete' ? 'Filmstrip ready' : phase === 'reset' ? 'New keyframe selected' : 'Tracking motion…',798,384,24,phase==='select'?palette.hole:palette.accent);
        text(phase === 'select' ? 'Capture this frame next.' : phase === 'complete' ? 'Three captured keyframes.' : phase === 'reset' ? 'Grid reseeded. Error reset.' : 'Holes grow between points.',798,420,20);
        ctx.fillStyle = palette.accent; ctx.beginPath(); ctx.arc(43,483,4,0,Math.PI*2); ctx.fill(); text('Tracked points',58,490,21);
        ctx.fillStyle = palette.hole; ctx.fillRect(435,475,15,15); text('Uncovered regions',461,490,21);
        text('Track → measure gaps → select → reset',36,539,23,palette.ink);
        drawFilmstrip();
    }
    function ballAt(time) {
        return { x: 360 + 230 * Math.sin(time * 0.58 - 0.9), y: 246 - 140 * Math.abs(Math.sin(time * 1.15 + 0.45)), r: 56 };
    }
    function currentScene() {
        return { zoom: sceneZoom * (1 + elapsed * 0.07), shift: sceneShift * (1 + elapsed * 0.07) + elapsed * 4, time: sceneTime, ball: ballAt(sceneTime) };
    }
    // Every source frame and filmstrip thumbnail uses this same scene renderer.
    function renderScene(c, state, stylized) {
        c.fillStyle = palette.surface; c.fillRect(0,0,W,H);
        c.save(); c.translate(W/2 + state.shift,H/2); c.scale(state.zoom,state.zoom); c.translate(-W/2,-H/2);
        c.fillStyle = palette.background; c.fillRect(-W,270,W*3,H);
        c.strokeStyle = stylized ? palette.wire : palette.border; c.lineWidth = 2;
        for (let x=-W; x<W*2; x+=90) { c.beginPath(); c.moveTo(x,270); c.lineTo(360+(x-360)*1.5,450); c.stroke(); }
        for (let y=270; y<450; y+=22) { c.beginPath(); c.moveTo(-W,y); c.lineTo(W*2,y); c.stroke(); }
        c.fillStyle = stylized ? palette.wire : palette.border; c.fillRect(65,160,75,110); c.fillRect(595,115,55,155);
        c.strokeStyle = palette.muted; c.strokeRect(65,160,75,110); c.strokeRect(595,115,55,155);
        c.restore();
        const b=state.ball;
        c.fillStyle = palette.shadow; c.beginPath(); c.ellipse(b.x,310,44+(b.y-106)*0.1,8,0,0,Math.PI*2); c.fill();
        const gradient=c.createRadialGradient(b.x-20,b.y-23,3,b.x,b.y,b.r);
        gradient.addColorStop(0,stylized?palette.highlight:palette.ink);
        gradient.addColorStop(0.5,stylized?palette.accent:palette.muted);
        gradient.addColorStop(1,stylized?palette.background:palette.border);
        c.fillStyle=gradient; c.beginPath(); c.arc(b.x,b.y,b.r,0,Math.PI*2); c.fill();
        c.save(); c.beginPath(); c.arc(b.x,b.y,b.r,0,Math.PI*2); c.clip();
        c.strokeStyle=stylized?palette.highlight:palette.ink; c.lineWidth=4;
        c.beginPath(); c.ellipse(b.x,b.y,21,b.r+5,Math.sin(state.time)*0.6,0,Math.PI*2); c.stroke();
        c.beginPath(); c.ellipse(b.x,b.y,b.r+5,16,-0.35,0,Math.PI*2); c.stroke(); c.restore();
    }
    function thumbnail(state,x,y,w,h,stylized) {
        ctx.save(); ctx.beginPath(); ctx.rect(x,y,w,h); ctx.clip(); ctx.translate(x,y); ctx.scale(w/W,h/H); renderScene(ctx,state,stylized); ctx.restore();
    }
    function drawFilmstrip() {
        text('02 / STYLIZE THE FILMSTRIP',36,596,21,palette.accent);
        text('Captured keyframes, stylized together.',36,632,23,palette.ink);
        ctx.strokeStyle=palette.accent; ctx.lineWidth=2; ctx.strokeRect(30,650,1140,225);
        for(let i=0;i<3;i++) {
            const x=40+i*376, state=selected[i];
            ctx.fillStyle=palette.surface; ctx.fillRect(x,660,360,168);
            if(state) thumbnail(state,x,660,360,168,true);
            else text('Waiting for selection…',x+43,754,22);
            text(state ? `Keyframe ${i+1} · ${state.time.toFixed(1)}s` : `Keyframe ${i+1}`,x+8,858,21,state?palette.accent:palette.muted);
        }
        text('03 / SPLIT → PROPAGATE → BLEND',36,922,21,palette.accent);
        text(selected.length===3 ? 'Fill the frames between keyframes.' : 'Waiting for three keyframes…',36,956,23,palette.ink);
        for(let i=0;i<7;i++) {
            const x=36+i*164;
            ctx.fillStyle=palette.surface; ctx.fillRect(x,980,148,69);
            if(selected.length===3) {
                const time=selected[0].time+(selected[2].time-selected[0].time)*i/6;
                const pair=time<=selected[1].time?[selected[0],selected[1]]:[selected[1],selected[2]];
                const f=(time-pair[0].time)/(pair[1].time-pair[0].time);
                thumbnail({time,ball:ballAt(time),zoom:pair[0].zoom+(pair[1].zoom-pair[0].zoom)*f,shift:pair[0].shift+(pair[1].shift-pair[0].shift)*f},x,980,148,69,true);
            }
        }
    }
    function reset() {
        elapsed = 0; seedBall = ballAt(sceneTime);
        seeds.forEach(p => { p.onBall = Math.hypot(p.x-seedBall.x,p.y-seedBall.y)<seedBall.r; p.occluded = false; });
        points = seeds.map(p => ({ ...p, seed:p })); measure();
    }
    function startOver() {
        keyframe=1; sceneZoom=1; sceneShift=0; sceneTime=0; phase='track'; hold=0; reset(); selected=[currentScene()];
    }
    function tick(now) {
        raf = 0;
        const dt = last ? Math.min((now-last)/1000,0.05) : 0; last = now;
        if (phase === 'track') {
            elapsed += dt; sceneTime += dt;
            const ball=ballAt(sceneTime);
            points = seeds.map(p => {
                const q = p.onBall ? {x:p.x+ball.x-seedBall.x,y:p.y+ball.y-seedBall.y,seed:p} : {x:W/2+(p.x-W/2)*(1+elapsed*0.07)+elapsed*4,y:H/2+(p.y-H/2)*(1+elapsed*0.07),seed:p};
                // A background point hidden by the ball cannot cover a newly exposed region later.
                if (!p.onBall && Math.hypot(q.x-ball.x,q.y-ball.y)<ball.r) p.occluded=true;
                return q;
            }).filter(p => !p.seed.occluded);
            measure();
            if (loss >= threshold) { phase = 'select'; hold = 0; announce(`${Math.round(threshold * 100)}% loss. Selecting keyframe.`); }
        } else {
            hold += dt;
            if (phase === 'select' && hold >= 1.2) {
                selected.push(currentScene());
                sceneZoom *= 1 + elapsed * 0.07;
                sceneShift = sceneShift * (1 + elapsed * 0.07) + elapsed * 4;
                keyframe++; reset(); phase = keyframe === 3 ? 'complete' : 'reset'; hold = 0;
                announce(`Keyframe ${keyframe} captured. Grid reset.`);
            } else if (phase === 'complete' && hold >= 4) {
                startOver(); announce('Replaying.');
            } else if (phase === 'reset' && hold >= 1.2) { phase = 'track'; announce('Rose = uncovered area.'); }
        }
        draw(); schedule();
    }
    function schedule() {
        if (!paused && visible && !document.hidden && !printing) { if (!raf) raf = requestAnimationFrame(tick); }
        else { cancelAnimationFrame(raf); raf = 0; last = 0; }
        pause.textContent = paused ? 'Play animation' : 'Pause animation';
    }
    listen(pause,'click',() => { paused = !paused; schedule(); });
    listen(demo.querySelector('[data-flow-replay]'),'click',() => {
        startOver(); last = 0; draw();
        announce('Replaying.'); schedule();
    });
    listen(darkMode,'change',() => { palette = readPalette(); measure(); draw(); });
    listen(reduced,'change',() => { paused = reduced.matches; schedule(); });
    listen(document,'visibilitychange',schedule);
    listen(window,'beforeprint',() => { printing = true; palette = readPalette(); measure(); draw(); schedule(); });
    listen(window,'afterprint',() => { printing = false; palette = readPalette(); measure(); draw(); schedule(); });
    const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; schedule(); });
    observer.observe(demo);
    demo.querySelector('.drip-flow-controls').hidden = false;
    startOver(); draw(); schedule();
    return () => { abort.abort(); observer.disconnect(); cancelAnimationFrame(raf); };
}
let disposeDripFlow = mountDripOpticalFlow();
document.addEventListener('research:before-swap', () => disposeDripFlow());
document.addEventListener('research:after-swap', () => { disposeDripFlow = mountDripOpticalFlow(); });
